export const FormFieldType = {
  TEXT: 'TEXT',
  EMAIL: 'EMAIL',
  DATE: 'DATE',
  SELECT: 'SELECT',
  CURRENCY: 'CURRENCY',
  NUMBER: 'NUMBER',
  RADIO: 'RADIO',
  CHECKBOX: 'CHECKBOX',
  RANGE: 'RANGE',
  COLOR: 'COLOR',
  URL: 'URL',
  TOGGLE: 'TOGGLE',
  PHONE: 'PHONE',
  FILE_UPLOAD: 'FILE_UPLOAD',
  /** Holds no value: a clickable button that runs automations. */
  BUTTON: 'BUTTON',
  /** Multi-line text. */
  PARAGRAPH: 'PARAGRAPH',
  DATETIME: 'DATETIME',
  TIME: 'TIME',
  /** Whole-number star rating, 1 to options.max. */
  RATING: 'RATING',
  PERCENT: 'PERCENT',
  /** Layout elements hold no value: they only shape the form. */
  SECTION_HEADING: 'SECTION_HEADING',
  STATIC_TEXT: 'STATIC_TEXT',
  DIVIDER: 'DIVIDER',
  LINE_BREAK: 'LINE_BREAK',
  PAGE_BREAK: 'PAGE_BREAK',
  /** A link to one email in a connected mailbox; automations can reply to it. */
  EMAIL_MESSAGE: 'EMAIL_MESSAGE',
} as const

export type FormFieldType = (typeof FormFieldType)[keyof typeof FormFieldType]

export const RoleType = {
  ADMIN: 'ADMIN',
  USER: 'USER',
  DEVELOPER: 'DEVELOPER',
} as const

export type RoleType = (typeof RoleType)[keyof typeof RoleType]

export const ActionType = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  /** A call on a record of the group ended. */
  CALL_ENDED: 'CALL_ENDED',
  /** A disposition was set on a call on a record of the group. */
  CALL_DISPOSITION_SET: 'CALL_DISPOSITION_SET',
  /** A tag was added to a record of the group. */
  TAG_ADDED: 'TAG_ADDED',
  /** A tag was removed from a record of the group. */
  TAG_REMOVED: 'TAG_REMOVED',
  /** A person added a note to a record of the group. */
  NOTE_ADDED: 'NOTE_ADDED',
  /** A person clicked a button field of a record of the group. */
  BUTTON_CLICKED: 'BUTTON_CLICKED',
} as const

export type ActionType = (typeof ActionType)[keyof typeof ActionType]

export const AutomationActionType = {
  SEND_EMAIL: 'SEND_EMAIL',
  ADD_NOTE: 'ADD_NOTE',
  UPDATE_FIELD: 'UPDATE_FIELD',
  /** Notifies people in the app and/or by push. */
  SEND_NOTIFICATION: 'SEND_NOTIFICATION',
} as const

export type AutomationActionType =
  (typeof AutomationActionType)[keyof typeof AutomationActionType]

export const ActivityActionType = {
  CREATE: 'CREATE',
  UPDATE_FIELD: 'UPDATE_FIELD',
  DELETE: 'DELETE',
  ADD_NOTE: 'ADD_NOTE',
  FILE_UPLOAD: 'FILE_UPLOAD',
  COMPLETE: 'COMPLETE',
  REOPEN: 'REOPEN',
  /** A call on the record; `note` holds its summary and `callId` the call. */
  CALL: 'CALL',
  /** A tag was added to / removed from the record; `tagLabel` and `tagColorHex` describe it. */
  TAG_ADDED: 'TAG_ADDED',
  TAG_REMOVED: 'TAG_REMOVED',
  /** A button of the record was clicked; `fieldLabel` names it. */
  BUTTON_CLICKED: 'BUTTON_CLICKED',
  /** An email sent (or saved as a draft) from the record by an automation; `note` holds its text. */
  EMAIL_SENT: 'EMAIL_SENT',
} as const

export type ActivityActionType =
  (typeof ActivityActionType)[keyof typeof ActivityActionType]

export const SubscriptionStatus = {
  TRIALING: 'TRIALING',
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
} as const

export type SubscriptionStatus =
  (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus]

export const InviteStatus = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  EXPIRED: 'EXPIRED',
} as const

export type InviteStatus = (typeof InviteStatus)[keyof typeof InviteStatus]
export const RecordSourceType = {
  INTERNAL_USER: 'INTERNAL_USER',
  PUBLIC_FORM: 'PUBLIC_FORM',
  CSV_IMPORT: 'CSV_IMPORT',
} as const

export type RecordSourceType = (typeof RecordSourceType)[keyof typeof RecordSourceType]

export const FormPublicLinkStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  EXPIRED: 'EXPIRED',
  MAX_REACHED: 'MAX_REACHED',
} as const

export type FormPublicLinkStatus = (typeof FormPublicLinkStatus)[keyof typeof FormPublicLinkStatus]

export const DashboardWidgetType = {
  NUMBER_CARD: 'NUMBER_CARD',
  NUMBER_CARD_GROUP: 'NUMBER_CARD_GROUP',
  BAR_CHART: 'BAR_CHART',
  LINE_CHART: 'LINE_CHART',
  PIE_CHART: 'PIE_CHART',
  DONUT_CHART: 'DONUT_CHART',
  STACKED_BAR: 'STACKED_BAR',
  TABLE: 'TABLE',
  ACTIVITY_FEED: 'ACTIVITY_FEED',
  PROGRESS_BAR: 'PROGRESS_BAR',
  STATUS_BADGE_GROUP: 'STATUS_BADGE_GROUP',
} as const

export type DashboardWidgetType = (typeof DashboardWidgetType)[keyof typeof DashboardWidgetType]

export const VisibilityLevel = {
  GLOBAL: 'GLOBAL',
  ROLE: 'ROLE',
  USER: 'USER',
  ROLE_AND_USER: 'ROLE_AND_USER',
  CREATOR_ONLY: 'CREATOR_ONLY',
} as const

export type VisibilityLevel = (typeof VisibilityLevel)[keyof typeof VisibilityLevel]
